/** La parte de las bases de datos que no es pantalla: filtrar, ordenar, crear ids y opciones. */
import {
  COLORES,
  type Esquema,
  type FechaValor,
  type Fila,
  type Filtro,
  type Opcion,
  type Propiedad,
  type TipoProp,
  type Vista,
} from './tipos'

/** Un identificador corto que cumple lo que el servidor exige: minúsculas y números. */
export function idCorto(): string {
  return 'a' + Math.random().toString(36).slice(2, 8).padEnd(6, '0')
}

export function propiedadDe(esquema: Esquema, id: string): Propiedad | undefined {
  return esquema.properties.find((p) => p.id === id)
}

export function opcionDe(prop: Propiedad, id: unknown): Opcion | undefined {
  return prop.options?.find((o) => o.id === id)
}

/** Añade una opción a una propiedad de selección y devuelve el esquema nuevo y su id. */
export function conOpcionNueva(
  esquema: Esquema,
  propId: string,
  nombre: string,
): { esquema: Esquema; opcionId: string } {
  const opcionId = idCorto()
  const usados = esquema.properties.find((p) => p.id === propId)?.options?.length ?? 0
  const color = COLORES[(usados + 1) % COLORES.length]
  return {
    opcionId,
    esquema: {
      ...esquema,
      properties: esquema.properties.map((p) =>
        p.id === propId
          ? { ...p, options: [...(p.options ?? []), { id: opcionId, name: nombre.trim(), color }] }
          : p,
      ),
    },
  }
}

export function propiedadNueva(nombre: string, tipo: TipoProp): Propiedad {
  const base: Propiedad = { id: idCorto(), name: nombre.trim() || 'Propiedad', type: tipo }
  if (tipo === 'status') {
    base.options = [
      { id: idCorto(), name: 'Por hacer', color: 'gris' },
      { id: idCorto(), name: 'En curso', color: 'azul' },
      { id: idCorto(), name: 'Hecho', color: 'verde' },
    ]
  } else if (tipo === 'select' || tipo === 'multi_select') {
    base.options = []
  }
  return base
}

// ───────────────────────────── Filtros ─────────────────────────────

export const OPERADORES: Record<string, { op: string; nombre: string; sinValor?: boolean }[]> = {
  title: [
    { op: 'contains', nombre: 'contiene' },
    { op: 'is', nombre: 'es' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  text: [
    { op: 'contains', nombre: 'contiene' },
    { op: 'is', nombre: 'es' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  url: [
    { op: 'contains', nombre: 'contiene' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  number: [
    { op: 'is', nombre: '=' },
    { op: 'gt', nombre: '>' },
    { op: 'lt', nombre: '<' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  select: [
    { op: 'is', nombre: 'es' },
    { op: 'is_not', nombre: 'no es' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  multi_select: [
    { op: 'contains', nombre: 'contiene' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  date: [
    { op: 'is', nombre: 'es' },
    { op: 'before', nombre: 'es antes de' },
    { op: 'after', nombre: 'es después de' },
    { op: 'empty', nombre: 'está vacío', sinValor: true },
    { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
  ],
  checkbox: [
    { op: 'checked', nombre: 'está marcada', sinValor: true },
    { op: 'unchecked', nombre: 'no está marcada', sinValor: true },
  ],
}
OPERADORES.status = OPERADORES.select
OPERADORES.person = [
  { op: 'contains', nombre: 'incluye a' },
  { op: 'empty', nombre: 'está vacío', sinValor: true },
  { op: 'not_empty', nombre: 'no está vacío', sinValor: true },
]

export function operadoresDe(prop: Propiedad | 'title'): typeof OPERADORES[string] {
  return OPERADORES[prop === 'title' ? 'title' : prop.type]
}

function vacio(v: unknown): boolean {
  return v == null || v === '' || v === false || (Array.isArray(v) && v.length === 0)
}

function coincide(fila: Fila, f: Filtro, esquema: Esquema): boolean {
  const valor = f.prop === 'title' ? fila.title : fila.props[f.prop]
  const prop = f.prop === 'title' ? null : propiedadDe(esquema, f.prop)
  if (f.prop !== 'title' && !prop) return true            // la propiedad se borró: no filtra

  switch (f.op) {
    case 'empty': return vacio(valor)
    case 'not_empty': return !vacio(valor)
    case 'checked': return valor === true
    case 'unchecked': return valor !== true
  }
  if (f.value == null || f.value === '') return true      // filtro a medio rellenar: no filtra

  const texto = String(valor ?? '').toLowerCase()
  const buscado = String(f.value).toLowerCase()

  if (prop?.type === 'multi_select' || prop?.type === 'person') {
    return Array.isArray(valor) && valor.includes(f.value as string)
  }
  if (prop?.type === 'date') {
    const inicio = (valor as FechaValor | undefined)?.start?.slice(0, 10)
    if (!inicio) return false
    const dia = String(f.value)
    return f.op === 'is' ? inicio === dia : f.op === 'before' ? inicio < dia : inicio > dia
  }
  if (prop?.type === 'number') {
    if (typeof valor !== 'number') return false
    const n = Number(f.value)
    return f.op === 'is' ? valor === n : f.op === 'gt' ? valor > n : valor < n
  }
  if (prop?.type === 'select' || prop?.type === 'status') {
    return f.op === 'is_not' ? valor !== f.value : valor === f.value
  }
  return f.op === 'is' ? texto === buscado : texto.includes(buscado)
}

/** Lo que enseña una vista: las filas que pasan sus filtros, en su orden. */
export function aplicarVista(filas: Fila[], vista: Vista, esquema: Esquema): Fila[] {
  let salida = (vista.filters ?? []).length
    ? filas.filter((fila) => (vista.filters ?? []).every((f) => coincide(fila, f, esquema)))
    : filas.slice()

  const orden = vista.sort
  if (orden) {
    const prop = orden.prop === 'title' ? null : propiedadDe(esquema, orden.prop)
    const clave = (fila: Fila): string | number => {
      if (orden.prop === 'title') return fila.title.toLowerCase()
      const v = fila.props[orden.prop]
      if (v == null) return ''
      if (prop?.type === 'number' && typeof v === 'number') return v
      if (prop?.type === 'date') return (v as FechaValor).start ?? ''
      if (prop?.type === 'select' || prop?.type === 'status') {
        const i = prop.options?.findIndex((o) => o.id === v) ?? -1
        return i < 0 ? 1e9 : i
      }
      if (Array.isArray(v)) return v.length
      return String(v).toLowerCase()
    }
    const signo = orden.dir === 'desc' ? -1 : 1
    salida = salida.sort((a, b) => {
      const x = clave(a)
      const y = clave(b)
      // Lo vacío va siempre al final, ordenes como ordenes.
      if (x === '' && y !== '') return 1
      if (y === '' && x !== '') return -1
      return x < y ? -signo : x > y ? signo : 0
    })
  }
  return salida
}

export function fechaCorta(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
}
